import { memo, useCallback, useMemo } from 'react';
import { createSingleSelectSection, FilterSlideout } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

/**
 * "All filters" slideout of the member Resources tab: the Type filter, edited as a draft that is
 * applied with Apply and discarded when the slideout closes (the same contract as the Members list).
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.draftType '' = every type
 * @param {(type: string) => void} props.onDraftTypeChange
 * @param {{ value: string, label: string }[]} props.typeOptions
 * @param {() => void} props.onApply
 */
function ResourcesFiltersSlideout({ isOpen, onClose, draftType, onDraftTypeChange, typeOptions, onApply }) {
  const { t } = useTranslation('people-access');

  const sections = useMemo(
    () => [
      createSingleSelectSection({
        id: 'type',
        title: t('memberTabs.resources.typeFilter'),
        value: draftType,
        allValue: '',
        options: typeOptions,
        onChange: onDraftTypeChange,
      }),
    ],
    [t, draftType, typeOptions, onDraftTypeChange],
  );

  const slideoutI18n = useMemo(
    () => ({
      close: t('members.filters.slideoutClose'),
      clearAll: t('members.filters.slideoutClear'),
      apply: t('members.filters.slideoutApply'),
    }),
    [t],
  );

  const handleClearAll = useCallback(() => onDraftTypeChange(''), [onDraftTypeChange]);
  const handleApply = useCallback(() => {
    onApply();
    onClose();
  }, [onApply, onClose]);

  return (
    <FilterSlideout
      isOpen={isOpen}
      onClose={onClose}
      title={t('members.filters.title')}
      i18n={slideoutI18n}
      sections={sections}
      onClearAll={handleClearAll}
      onApply={handleApply}
    />
  );
}

export default memo(ResourcesFiltersSlideout);
